import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Instagram, Youtube, ArrowUpRight, Check } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { NotifyForm } from "@/components/NotifyForm";

const INSTAGRAM = "https://www.instagram.com/itsmy_network?igsh=MThrdnUzbjNsejdiOA==";
const YOUTUBE = "https://youtube.com/@e3_youngbugg?si=ANBc11VylC53jVVH";

const HERO_BG =
  "https://images.pexels.com/photos/5504137/pexels-photo-5504137.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 + i * 0.12 },
  }),
};

export function Hero({ onExplore, onSubscribe }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  const lines = ["IT'S", "MY", "NETWORK"];

  return (
    <section ref={ref} className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-[#050505]/40" />
      </motion.div>

      <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-16 pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 1 }}
          className="font-mono-imn text-xs md:text-sm tracking-[0.4em] text-gold uppercase mb-6"
        >
          The Network · Connect · Collaborate · Elevate
        </motion.p>

        <h1 className="font-display font-bold uppercase leading-[0.85] text-[19vw] md:text-[15vw] lg:text-[13vw]">
          {lines.map((l, i) => (
            <span key={l} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block"
              >
                {l === "MY" ? <span className="text-gold">{l}</span> : l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 max-w-2xl"
        >
          <p className="text-white/70 text-sm md:text-base max-w-md">
            An independent TV network for original clips, shows and documentaries.
            Stream free previews or go premium for the full experience.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onExplore}
              data-testid="hero-explore-btn"
              className="bg-white text-black font-semibold px-7 py-3 text-sm uppercase tracking-wider hover:bg-white/90 active:scale-95 transition-all"
            >
              Start Watching
            </button>
            <button
              onClick={onSubscribe}
              data-testid="hero-subscribe-btn"
              className="border border-white/25 px-7 py-3 text-sm uppercase tracking-wider hover:bg-white/10 transition-colors"
            >
              Go Premium
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export function MarqueeStrip() {
  return (
    <div className="border-y border-white/10 py-6 bg-[#050505]" data-testid="marquee-strip">
      <Marquee speed={40} gradient={false}>
        {Array.from({ length: 6 }).map((_, i) => (
          <span
            key={i}
            className="font-display text-4xl md:text-6xl font-bold uppercase mx-8 outline-text"
          >
            It's My Network — New Episodes — Premium Clips —
          </span>
        ))}
      </Marquee>
    </div>
  );
}

const CHAPTERS = [
  { n: "01", t: "Original Content", d: "Shows, clips and documentaries produced in-house by the network. Real stories, real culture." },
  { n: "02", t: "Free & Premium", d: "Watch curated previews for free. Subscribe to unlock the full library of premium originals." },
  { n: "03", t: "Connect Everywhere", d: "Every drop links out to Instagram and YouTube so the movement travels beyond the screen." },
];

export function Manifesto() {
  return (
    <section className="px-6 md:px-16 py-28" data-testid="manifesto-section">
      <p className="font-mono-imn text-xs tracking-[0.3em] text-gold uppercase mb-4">
        The Manifesto
      </p>
      <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tight max-w-3xl leading-[0.95] mb-16">
        Built for creators. <span className="text-gold">Owned</span> by the culture.
      </h2>
      <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {CHAPTERS.map((c, i) => (
          <motion.div
            key={c.n}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.12 }}
            className="bg-[#050505] p-10"
          >
            <span className="font-display text-6xl font-bold outline-text block mb-6">
              {c.n}
            </span>
            <h3 className="font-display text-2xl font-medium uppercase mb-3">{c.t}</h3>
            <p className="text-white/50 text-sm leading-relaxed">{c.d}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

export function Pricing({ onSelect, showLivePass = false }) {
  const allPlans = [
    {
      id: "monthly", name: "Monthly", price: "$5.99", per: "/month",
      features: ["Full premium library", "New episodes weekly", "Cancel anytime", "Ad-free streaming"],
    },
    {
      id: "yearly", name: "Yearly", price: "$49.99", per: "/year", best: true,
      features: ["Everything in Monthly", "Save over 30%", "Priority new drops", "Ad-free streaming"],
    },
    {
      id: "live", name: "Live Pass", price: "$7.99", per: "/month", live: true,
      features: ["All Local Cable channels", "Separate from subscription", "Live events & shows", "Cancel anytime"],
    },
  ];
  const plans = showLivePass ? allPlans : allPlans.filter((p) => !p.live);
  return (
    <section id="pricing" className="relative px-6 md:px-16 py-28 overflow-hidden" data-testid="pricing-section">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none flex items-center">
        <span className="font-display text-[20vw] font-bold uppercase whitespace-nowrap">
          Go Premium
        </span>
      </div>
      <div className="relative">
        <p className="font-mono-imn text-xs tracking-[0.3em] text-gold uppercase mb-4">
          Membership
        </p>
        <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tight mb-3 max-w-2xl leading-[0.95]">
          Unlock the full network
        </h2>
        {showLivePass ? (
          <p className="text-white/50 text-sm max-w-xl mb-14">
            Premium unlocks the on-demand library. The <span className="text-gold">Local Cable</span> channels
            are a separate <span className="text-gold">Live Pass</span> ($7.99/mo).
          </p>
        ) : (
          <p className="text-white/50 text-sm max-w-xl mb-14">
            Premium unlocks the full on-demand library of originals and exclusives.
          </p>
        )}
        <div className={`grid gap-6 ${showLivePass ? "md:grid-cols-3 max-w-5xl" : "md:grid-cols-2 max-w-4xl"}`}>
          {plans.map((p) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={`relative border p-10 ${
                p.best ? "border-gold bg-white/[0.03]" : p.live ? "border-gold/40 bg-gold/[0.03]" : "border-white/10 bg-[#0A0A0A]"
              }`}
            >
              {p.best && (
                <span className="absolute top-6 right-6 bg-gold text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                  Best Value
                </span>
              )}
              {p.live && (
                <span className="absolute top-6 right-6 border border-gold text-gold text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                  Live
                </span>
              )}
              <p className="font-mono-imn text-xs tracking-widest uppercase text-white/50 mb-4">
                {p.name}
              </p>
              <div className="flex items-end gap-1 mb-8">
                <span className="font-display text-6xl font-bold">{p.price}</span>
                <span className="text-white/40 mb-2 text-sm">{p.per}</span>
              </div>
              <ul className="space-y-3 mb-10">
                {p.features.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-white/70">
                    <Check size={16} className="text-gold shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => onSelect(p.id)}
                data-testid={`plan-${p.id}-btn`}
                className={`w-full py-3 text-sm font-semibold uppercase tracking-wider transition-all active:scale-[0.98] ${
                  p.best
                    ? "bg-white text-black hover:bg-white/90"
                    : p.live
                    ? "bg-gold text-black hover:opacity-90"
                    : "border border-white/25 hover:bg-white/10"
                }`}
              >
                {p.live ? "Get Live Pass" : `Choose ${p.name}`}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ComingSoon({ show, onOpen }) {
  if (!show) return null;
  return (
    <section
      id="coming-soon"
      className="relative px-6 md:px-16 py-28 border-y border-white/10 overflow-hidden"
      data-testid="coming-soon-section"
    >
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none flex items-center">
        <span className="font-display text-[18vw] font-bold uppercase whitespace-nowrap">
          Coming Soon
        </span>
      </div>
      <div className="relative grid md:grid-cols-[minmax(0,420px)_1fr] gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative group cursor-pointer"
          onClick={() => onOpen?.(show)}
          data-testid="coming-soon-poster"
        >
          <div className="absolute -inset-2 border border-gold/40 -z-10" />
          <img
            src={show.thumbnail}
            alt={show.title}
            className="w-full h-auto border border-white/10 transition-transform duration-700 group-hover:scale-[1.02]"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <p className="font-mono-imn text-xs tracking-[0.3em] text-gold uppercase mb-4">
            Coming Soon · Season One
          </p>
          <h2 className="font-display text-5xl md:text-7xl font-bold uppercase tracking-tight leading-[0.9] mb-6">
            {show.title}
          </h2>
          <p className="text-white/60 text-base max-w-xl mb-8 leading-relaxed">
            {show.description}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => onOpen?.(show)}
              data-testid="coming-soon-trailer-btn"
              className="bg-white text-black font-semibold px-7 py-3 text-sm uppercase tracking-wider hover:bg-white/90 active:scale-95 transition-all"
            >
              Watch Teaser
            </button>
            <span className="border border-gold text-gold px-5 py-3 text-xs font-semibold uppercase tracking-widest self-center">
              Every variable changes the outcome
            </span>
          </div>
          <div className="mt-8">
            <p className="font-mono-imn text-[11px] tracking-widest text-white/40 uppercase mb-3">
              Get notified when it drops
            </p>
            <NotifyForm show={show.title} testid="notify-variablex" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-white/10 px-6 md:px-16 py-16" data-testid="footer">
      <div className="grid md:grid-cols-2 gap-12 items-start">
        <div>
          <img src={LOGO_URL} alt="It's My Network" className="h-16 w-auto object-contain mb-6" />
          <p className="text-white/50 text-sm max-w-sm">
            It's My Network — connect, collaborate, elevate. An independent streaming
            platform for original content.
          </p>
        </div>
        <div className="md:justify-self-end">
          <p className="font-mono-imn text-xs tracking-[0.3em] text-gold uppercase mb-5">
            Follow The Network
          </p>
          <div className="flex flex-col gap-3">
            <a
              href={INSTAGRAM} target="_blank" rel="noreferrer"
              data-testid="footer-instagram"
              className="group flex items-center gap-3 text-lg hover:text-gold transition-colors"
            >
              <Instagram size={20} /> Instagram
              <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
            <a
              href={YOUTUBE} target="_blank" rel="noreferrer"
              data-testid="footer-youtube"
              className="group flex items-center gap-3 text-lg hover:text-gold transition-colors"
            >
              <Youtube size={20} /> YouTube
              <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>
      </div>
      <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-2 font-mono-imn text-[11px] tracking-widest uppercase text-white/30">
        <span>© {new Date().getFullYear()} It's My Network</span>
        <span>Connect · Collaborate · Elevate</span>
      </div>
    </footer>
  );
}
